import { useState, useEffect } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { useAuth } from '@/contexts/AuthContext'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Badge } from '@/components/ui/badge'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { ChevronLeft, ChevronRight, Code2, Calendar, Copy, Check } from 'lucide-react'
import { api } from '@/lib/api'

interface ArtifactType {
  typeId: string
  name: string
  description?: string
  llm_summary?: string
  version?: number
  schema: any
}

interface ArtifactInstance {
  id: string
  typeId: string
  type: string
  name?: string
  description?: string
  data: any
  version: number
  createdAt: string
  updatedAt: string
  user?: {
    id: string
    email: string
    firstName?: string
    lastName?: string
  }
  agent?: {
    id: string
    name: string
  }
}

interface ArtifactsResponse {
  artifacts: ArtifactInstance[]
  total: number
  page: number
  totalPages: number
}

// Separate component for schema display to avoid hooks in render function
function SchemaDisplay({ schema }: { schema: any }) {
  const [schemaView, setSchemaView] = useState<'summary' | 'full'>('full')
  
  return (
    <div className="space-y-3">
      <Tabs value={schemaView} onValueChange={(v) => setSchemaView(v as 'summary' | 'full')}>
        <TabsList className="grid w-full grid-cols-2 h-8">
          <TabsTrigger value="summary" className="text-xs">Summary</TabsTrigger>
          <TabsTrigger value="full" className="text-xs">Full Schema</TabsTrigger>
        </TabsList>
        
        <TabsContent value="summary" className="mt-3">
          {schema.properties && Object.keys(schema.properties).length > 0 ? (
            <div className="bg-muted rounded p-3 space-y-1 overflow-auto max-h-96">
              {Object.entries(schema.properties).map(([key, value]: [string, any]) => (
                <div key={key} className="text-xs">
                  <span className="font-medium">{key}:</span>{' '}
                  <span className="text-muted-foreground">
                    {value.type || 'any'}
                    {value.optional && ' (optional)'}
                    {value.description && ` - ${value.description}`}
                    {value.enum && ` [${value.enum.join(', ')}]`}
                  </span>
                </div>
              ))}
              {schema.required && schema.required.length > 0 && (
                <div className="text-xs mt-2 pt-2 border-t border-border">
                  <span className="text-muted-foreground">Required fields: </span>
                  <span className="font-medium">{schema.required.join(', ')}</span>
                </div>
              )}
            </div>
          ) : (
            <div className="text-xs text-muted-foreground">No properties defined</div>
          )}
        </TabsContent>
        
        <TabsContent value="full" className="mt-3">
          <pre className="text-xs bg-muted p-3 rounded overflow-auto max-h-96 border border-border">
            {JSON.stringify(schema, null, 2)}
          </pre>
        </TabsContent>
      </Tabs>
    </div>
  )
}

// Modal component for artifact data display
interface ArtifactDataModalProps {
  selectedArtifact: ArtifactInstance | null
  artifactTypes: ArtifactType[]
  onClose: () => void
}

function ArtifactDataModal({ selectedArtifact, artifactTypes, onClose }: ArtifactDataModalProps) {
  const [copiedJson, setCopiedJson] = useState(false)
  const [activeTab, setActiveTab] = useState<'data' | 'schema'>('data')
  
  // Find the schema for the selected artifact's type
  const artifactSchema = selectedArtifact 
    ? artifactTypes.find(t => t.typeId === selectedArtifact.typeId)?.schema
    : null

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }

  return (
    <Dialog
      open={!!selectedArtifact}
      onOpenChange={(open) => {
        if (!open) {
          onClose()
        }
      }}
    >
      <DialogContent className="max-w-3xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle>
            {selectedArtifact?.name || `Artifact ${selectedArtifact?.id.slice(0, 8)}`}
          </DialogTitle>
          <DialogDescription>
            {selectedArtifact?.description || 'Artifact details and JSON data'}
          </DialogDescription>
        </DialogHeader>
        
        {selectedArtifact && (
          <div className="flex-1 overflow-y-auto">
            <div className="space-y-4 p-1">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Type</h4>
                  <p className="text-sm mt-1">{selectedArtifact.type} (ID: {selectedArtifact.typeId})</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Version</h4>
                  <p className="text-sm mt-1">{selectedArtifact.version}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Created</h4>
                  <p className="text-sm mt-1">{formatDate(selectedArtifact.createdAt)}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Last Updated</h4>
                  <p className="text-sm mt-1">{formatDate(selectedArtifact.updatedAt)}</p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">Created By</h4>
                  <p className="text-sm mt-1">
                    {selectedArtifact.user 
                      ? `${selectedArtifact.user.firstName && selectedArtifact.user.lastName 
                          ? `${selectedArtifact.user.firstName} ${selectedArtifact.user.lastName}` 
                          : selectedArtifact.user.email} (User)`
                      : selectedArtifact.agent 
                        ? `${selectedArtifact.agent.name} (Agent)`
                        : 'Unknown'}
                  </p>
                </div>
                <div>
                  <h4 className="text-sm font-medium text-muted-foreground">ID</h4>
                  <p className="text-sm mt-1 font-mono text-xs">{selectedArtifact.id}</p>
                </div>
              </div>
              
              <div className="space-y-2">
                <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'data' | 'schema')}>
                  <TabsList className="grid w-full grid-cols-2">
                    <TabsTrigger value="data">JSON Data</TabsTrigger>
                    <TabsTrigger value="schema">Schema</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="data" className="mt-4">
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <h4 className="text-sm font-medium text-muted-foreground">JSON Data</h4>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            navigator.clipboard.writeText(JSON.stringify(selectedArtifact.data, null, 2))
                            setCopiedJson(true)
                            setTimeout(() => setCopiedJson(false), 2000)
                          }}
                        >
                          {copiedJson ? (
                            <>
                              <Check className="w-4 h-4 mr-1" />
                              Copied!
                            </>
                          ) : (
                            <>
                              <Copy className="w-4 h-4 mr-1" />
                              Copy JSON
                            </>
                          )}
                        </Button>
                      </div>
                      <pre className="text-xs bg-muted p-4 rounded overflow-auto max-h-96">
                        {JSON.stringify(selectedArtifact.data, null, 2)}
                      </pre>
                    </div>
                  </TabsContent>
                  
                  <TabsContent value="schema" className="mt-4">
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-muted-foreground">Artifact Schema</h4>
                      {artifactSchema ? (
                        <SchemaDisplay schema={artifactSchema} />
                      ) : (
                        <div className="text-sm text-muted-foreground p-4 bg-muted rounded">
                          Schema not available for this artifact type
                        </div>
                      )}
                    </div>
                  </TabsContent>
                </Tabs>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export function ArtifactsPage() {
  const { isAdmin } = useAuth()
  const navigate = useNavigate()
  const { artifactId } = useParams()
  const location = useLocation()
  const [activeTab, setActiveTab] = useState('types')
  const [artifactTypes, setArtifactTypes] = useState<ArtifactType[]>([])
  const [artifacts, setArtifacts] = useState<ArtifactInstance[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Pagination and filtering state
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [userFilter, setUserFilter] = useState('')
  const [sortBy, setSortBy] = useState('createdAt')
  const [sortOrder, setSortOrder] = useState<'ASC' | 'DESC'>('DESC')
  
  // Selected artifact for JSON viewer
  const [selectedArtifact, setSelectedArtifact] = useState<ArtifactInstance | null>(null)

  // Initialize active tab from route
  useEffect(() => {
    if (location.pathname.startsWith('/artifacts/instances')) {
      setActiveTab('instances')
    } else if (location.pathname.startsWith('/artifacts')) {
      // default to types when just /artifacts
      setActiveTab('types')
    }
  }, [location.pathname])

  // Fetch artifact types
  const fetchArtifactTypes = async () => {
    try {
      const response = await api.get('/admin/artifact-types')
      setArtifactTypes(response.data.types)
    } catch (err) {
      console.error('Error fetching artifact types:', err)
      setError('Failed to load artifact types')
    }
  }

  // Fetch artifact instances
  const fetchArtifacts = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20',
        orderBy: sortBy,
        order: sortOrder
      })
      
      if (userFilter) {
        params.append('userId', userFilter)
      }

      const response = await api.get<ArtifactsResponse>(
        `/admin/artifacts?${params}`
      )
      
      setArtifacts(response.data.artifacts)
      setTotal(response.data.total)
      setTotalPages(response.data.totalPages)
    } catch (err) {
      console.error('Error fetching artifacts:', err)
      setError('Failed to load artifacts')
    } finally {
      setLoading(false)
    }
  }

  // Fetch single artifact by id (for direct URL open)
  const fetchArtifactById = async (id: string) => {
    try {
      const response = await api.get(`/artifacts/${id}`)
      return response.data as ArtifactInstance
    } catch (err) {
      console.error('Error fetching artifact by id:', err)
      return null
    }
  }

  // Always fetch artifact types on mount (needed for schema display)
  useEffect(() => {
    fetchArtifactTypes()
  }, [])

  useEffect(() => {
    if (activeTab === 'instances') {
      fetchArtifacts()
    }
  }, [activeTab, page, sortBy, sortOrder, userFilter])

  // When route contains an artifactId, ensure the dialog opens on that artifact
  useEffect(() => {
    const ensureSelected = async () => {
      if (!artifactId) {
        setSelectedArtifact(null)
        return
      }

      // Make sure we are on instances tab for this route
      if (activeTab !== 'instances') setActiveTab('instances')

      // Try to find in current list; otherwise fetch directly
      const inList = artifacts.find(a => a.id === artifactId)
      if (inList) {
        setSelectedArtifact(inList)
      } else {
        const fetched = await fetchArtifactById(artifactId)
        if (fetched) setSelectedArtifact(fetched)
      }
    }
    ensureSelected()
    // Only re-run when artifactId or artifacts list changes
  }, [artifactId, artifacts])

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString()
  }



  // Check if user is admin
  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-[50vh]">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle>Access Denied</CardTitle>
            <CardDescription>
              You need admin privileges to access this page.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-3xl font-bold tracking-tight">Artifacts</h2>
        <p className="text-muted-foreground">
          Manage artifact types and instances
        </p>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => {
          setActiveTab(v)
          if (v === 'instances') {
            // Navigate to instances tab route without a specific id
            if (!location.pathname.startsWith('/artifacts/instances')) {
              navigate('/artifacts/instances', { replace: true })
            }
          } else {
            // Back to base artifacts route
            if (location.pathname !== '/artifacts') {
              navigate('/artifacts', { replace: true })
            }
          }
        }}
      >
        <TabsList className="grid w-full grid-cols-2 max-w-md">
          <TabsTrigger value="types">Artifact Types</TabsTrigger>
          <TabsTrigger value="instances">Artifact Instances</TabsTrigger>
        </TabsList>

        <TabsContent value="types" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Available Artifact Types</CardTitle>
              <CardDescription>
                All registered artifact types with their schemas and descriptions
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {artifactTypes.map((type) => (
                  <Card key={type.typeId}>
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-lg">{type.name}</CardTitle>
                          <CardDescription>{type.description}</CardDescription>
                        </div>
                        <Badge variant="secondary">v{type.version || 1}</Badge>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {type.llm_summary && (
                        <div>
                          <h4 className="text-sm font-medium mb-1">LLM Summary</h4>
                          <p className="text-sm text-muted-foreground">{type.llm_summary}</p>
                        </div>
                      )}
                      <div>
                        <h4 className="text-sm font-medium mb-1">Schema</h4>
                        <SchemaDisplay schema={type.schema} />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="instances" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Artifact Instances</CardTitle>
              <CardDescription>
                All artifact instances in the system
              </CardDescription>
            </CardHeader>
            <CardContent>
              {/* Filters */}
              <div className="flex gap-4 mb-4">
                <Input
                  placeholder="Filter by user ID..."
                  value={userFilter}
                  onChange={(e) => setUserFilter(e.target.value)}
                  className="max-w-xs"
                />
                <Select value={sortBy} onValueChange={setSortBy}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Sort by..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="createdAt">Created Date</SelectItem>
                    <SelectItem value="updatedAt">Updated Date</SelectItem>
                    <SelectItem value="type">Type</SelectItem>
                    <SelectItem value="version">Version</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortOrder} onValueChange={(v) => setSortOrder(v as 'ASC' | 'DESC')}>
                  <SelectTrigger className="w-[120px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="DESC">Newest First</SelectItem>
                    <SelectItem value="ASC">Oldest First</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Table */}
              {loading ? (
                <div className="text-center py-8 text-muted-foreground">Loading artifacts...</div>
              ) : error ? (
                <div className="text-center py-8 text-destructive">{error}</div>
              ) : (
                <>
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Type</TableHead>
                        <TableHead>Name</TableHead>
                        <TableHead>Creator</TableHead>
                        <TableHead>Version</TableHead>
                        <TableHead>Created</TableHead>
                        <TableHead>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {artifacts.map((artifact) => (
                        <TableRow key={artifact.id}>
                          <TableCell>
                            <Badge>{artifact.type}</Badge>
                          </TableCell>
                          <TableCell className="font-medium">
                            {artifact.name || artifact.id.slice(0, 8)}
                          </TableCell>
                          <TableCell>
                            {artifact.user ? (
                              <div className="text-sm">
                                <div>{artifact.user.firstName && artifact.user.lastName 
                                  ? `${artifact.user.firstName} ${artifact.user.lastName}`
                                  : artifact.user.email}</div>
                                <div className="text-muted-foreground text-xs">User</div>
                              </div>
                            ) : artifact.agent ? (
                              <div className="text-sm">
                                <div>{artifact.agent.name}</div>
                                <div className="text-muted-foreground text-xs">Agent</div>
                              </div>
                            ) : (
                              'Unknown'
                            )}
                          </TableCell>
                          <TableCell>{artifact.version}</TableCell>
                          <TableCell className="text-sm">
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(artifact.createdAt)}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Button
                              variant={artifactId === artifact.id ? "default" : "ghost"}
                              size="sm"
                              onClick={() => {
                                if (artifactId === artifact.id) {
                                  navigate('/artifacts/instances', { replace: false })
                                } else {
                                  // Ensure instances tab and navigate to specific artifact
                                  if (activeTab !== 'instances') setActiveTab('instances')
                                  navigate(`/artifacts/instances/${artifact.id}`)
                                }
                              }}
                            >
                              <Code2 className="w-4 h-4 mr-1" />
                              {artifactId === artifact.id ? 'Hide' : 'View'} Data
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>


                  {/* Pagination */}
                  <div className="flex items-center justify-between mt-4">
                    <p className="text-sm text-muted-foreground">
                      Showing {artifacts.length} of {total} artifacts
                    </p>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                      >
                        <ChevronLeft className="w-4 h-4" />
                        Previous
                      </Button>
                      <span className="text-sm">
                        Page {page} of {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                      >
                        Next
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Artifact Data Modal */}
      <ArtifactDataModal
        selectedArtifact={selectedArtifact}
        artifactTypes={artifactTypes}
        onClose={() => {
          setSelectedArtifact(null)
          // When closing, remain on instances tab route
          if (location.pathname.startsWith('/artifacts/instances/')) {
            navigate('/artifacts/instances', { replace: true })
          }
        }}
      />
    </div>
  )
}
